import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo } from 'react';

export type Language = 'en' | 'nl';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    (localStorage.getItem('i18nextLng')?.substring(0, 2) as Language) || 'en'
  );

  useEffect(() => {
    // Update the state when the language changes
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng.substring(0, 2) as Language);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = useCallback((language: Language) => {
    i18n.changeLanguage(language);
    setCurrentLanguage(language);
  }, [i18n]);

  // Memoize the languages array to prevent it from being recreated on each render
  const languages = useMemo(() => [
    { 
      code: 'en', 
      name: 'English', 
      // Using Unicode flag characters for better compatibility
      flag: String.fromCodePoint(0x1F1EC, 0x1F1E7) // GB flag
    },
    { 
      code: 'nl', 
      name: 'Nederlands', 
      // Using Unicode flag characters for better compatibility
      flag: String.fromCodePoint(0x1F1F3, 0x1F1F1) // NL flag
    },
  ], []);

  // Return a memoized object to prevent unnecessary rerenders
  return useMemo(() => ({
    currentLanguage,
    changeLanguage,
    languages,
  }), [currentLanguage, changeLanguage, languages]);
}; 